/**
 * Plausibility bounds for the "Requested Ship Date" inputs.
 *
 * A native `<input type="date">` with no `min`/`max` will happily emit a
 * year-1426 date: typing digits into the year segment rolls them left
 * ("14" then "26" -> 0014 -> 0142 -> 1426). That is exactly how order
 * 9b28b61d reached Sage with UDF_REQUIREDBY='1426-08-14' on 2026-08-10.
 *
 * These bounds MIRROR SHIP_DATE_MAX_PAST_DAYS / SHIP_DATE_MAX_FUTURE_DAYS in
 * `services/orders/submission_validator.py`. Keep them in step — the server is
 * the real gate (it hard-blocks submission); this is the first line of defence
 * that keeps the date picker honest and marks a bad value invalid as it is
 * typed.
 */

export const SHIP_DATE_MAX_PAST_DAYS = 365;
export const SHIP_DATE_MAX_FUTURE_DAYS = 365 * 5;

export interface ShipDateBounds {
  /** Earliest acceptable date, as `YYYY-MM-DD`. */
  min: string;
  /** Latest acceptable date, as `YYYY-MM-DD`. */
  max: string;
}

/** Format a Date as `YYYY-MM-DD` in LOCAL time.
 *
 * `toISOString()` converts to UTC first, which shifts the calendar day for
 * anyone west of Greenwich (a date picked at 20:00 in New York would render as
 * tomorrow). The date inputs are local-calendar values, so build the string
 * from the local components.
 */
function toIsoDate(d: Date): string {
  const year = String(d.getFullYear()).padStart(4, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Bounds for a requested ship date, relative to `today` (default: now). */
export function shipDateBounds(today: Date = new Date()): ShipDateBounds {
  const min = new Date(today);
  min.setDate(min.getDate() - SHIP_DATE_MAX_PAST_DAYS);
  const max = new Date(today);
  max.setDate(max.getDate() + SHIP_DATE_MAX_FUTURE_DAYS);
  return { min: toIsoDate(min), max: toIsoDate(max) };
}

/** Today as `YYYY-MM-DD` in local time — the default when adding a ship date. */
export function todayIsoDate(today: Date = new Date()): string {
  return toIsoDate(today);
}
